// PipelineDetailModal — Reforma UX §6.8 / wireframe dlgPipe (PIPE-02..06).
//
// Modal de detalhe do pipeline sobre a lista, com:
//   - Header: nome + slug (copiar) + SWITCH ativar/desativar (também no card)
//     + meta scope / versão ativa.
//   - Abas (rótulos do HTML canônico): Etapas · Versões · Runs · Métricas.
//     * Etapas: editáveis — executor (agente/skill) + timeout + retry; editar
//       abre PipelineStepEditModal (PIPE-02/03: edição publica NOVA VERSÃO
//       imutável via POST /v1/pipelines/:id/versions; a nova vira a ativa).
//     * Versões: lista com badge ativa/arquivada/deprecada.
//     * Runs: "Demandas bloqueadas" (cards Rodar demanda + Ver detalhes,
//       posição canônica do HTML) + "Runs recentes" — run abre
//       PipelineRunDetailSheet (resumo + timeline de etapas + Abrir demanda).
//     * Métricas: contadores por status + Taxa de sucesso (PIPE-05:
//       completos/(completos+falhos), canceladas FORA) + tempo médio.
//   - Footer: Fechar + Nova versão (modal — POST /v1/pipelines/:id/versions
//     com o config da versão ativa como base; 404 degrada com toast claro
//     até o backend ser reiniciado com a rota).
//
// Aceita id OU slug (deep-link /pipelines/:id) — resolve via getPipeline e usa
// o id resolvido pra runs/realtime.

import { useEffect, useState } from 'react';
import { PipelineRunDetailSheet } from '@/components/raios/PipelineRunDetailSheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/Dialog';
import { Tabs } from '@/components/ui/Tabs';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import {
  ListOrdered,
  GitBranch,
  Activity,
  BarChart3,
  Copy,
  Check,
  Pencil,
  Clock,
  Workflow,
  Wrench,
} from 'lucide-react';
import { useSupabaseRealtime } from '@/hooks/useSupabaseRealtime';
import {
  getPipeline,
  listPipelineRuns,
  patchPipeline,
  type PipelineSummary,
  type PipelineVersionSummary,
  type PipelineRunSummary,
} from '@/lib/api-pipelines';
import { aggregateRuns, residualOthers, runStatusLabel, runStatusTone } from '@/lib/pipeline-counts';
import {
  normalizeSteps,
  successPct,
  avgCompletedDuration,
  type NormalizedStep,
} from './pipeline-metrics';
import { BlockedDemandsSection } from './BlockedDemandsSection';
import { RunDemandModal } from './RunDemandModal';
import { PipelineStepEditModal } from './PipelineStepEditModal';
import { PipelineNewVersionModal } from './PipelineNewVersionModal';
import type { Demand } from '@/lib/api';

interface Props {
  /** id OU slug — deep-link /pipelines/:id aceita ambos (getPipeline resolve). */
  pipelineIdOrSlug: string | null;
  open: boolean;
  onClose: () => void;
  /** Notifica a page (lista) após mutação (toggle ativo) pra re-sincronizar cards. */
  onChanged?: () => void;
}

/** UUID v1-v5 — decide se podemos paralelizar getPipeline + listPipelineRuns. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

type TabValue = 'steps' | 'versions' | 'runs' | 'metrics';

const TABS = [
  { value: 'steps', label: 'Etapas', icon: ListOrdered },
  { value: 'versions', label: 'Versões', icon: GitBranch },
  { value: 'runs', label: 'Runs', icon: Activity },
  { value: 'metrics', label: 'Métricas', icon: BarChart3 },
];

type PipelineDetail = PipelineSummary & { versions: PipelineVersionSummary[] };

function CopyIdButton({ value, title }: { value: string; title: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={(e) => {
        e.stopPropagation();
        void navigator.clipboard?.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex items-center justify-center p-1 rounded hover:bg-raios-elevated text-raios-muted"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-raios-success-text" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

/** Linha de etapa — wireframe .pstep: nº · nome · executor · timeout · editar. */
function StepRow({ step, onEdit }: { step: NormalizedStep; onEdit: () => void }) {
  const ExecIcon = step.executorKind === 'skill' ? Wrench : Workflow;
  const execTone = step.executorKind === 'skill'
    ? 'text-purple-600 dark:text-purple-400'
    : 'text-blue-600 dark:text-blue-400';
  return (
    <div className="flex items-center gap-3 px-4 py-3 border-b border-raios-border last:border-0">
      <span className="shrink-0 h-7 w-7 rounded-full bg-raios-elevated text-xs font-mono font-semibold text-raios-text-secondary flex items-center justify-center">
        {step.stepNumber}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-raios-text truncate">{step.name}</p>
        <div className="flex flex-wrap items-center gap-2 mt-0.5 text-xs">
          <span className={`inline-flex items-center gap-1 font-medium ${execTone}`}>
            <ExecIcon className="h-3.5 w-3.5" />
            {step.executorKind === 'skill' ? 'skill' : 'agente'} · {step.executorSlug}
          </span>
          {step.timeoutSeconds !== null && (
            <span className="inline-flex items-center gap-1 text-raios-muted">
              <Clock className="h-3 w-3" />
              timeout {step.timeoutSeconds}s
            </span>
          )}
          {step.retryOnFail !== null && (
            <span className="text-raios-muted">retry {step.retryOnFail}</span>
          )}
        </div>
      </div>
      <Button size="sm" variant="ghost" onClick={onEdit} title="Editar etapa (cria nova versão)">
        <Pencil className="h-3.5 w-3.5" />
        editar
      </Button>
    </div>
  );
}

/** Envelope do fetch — setado só em callbacks async (lint set-state-in-effect). */
interface LoadState {
  /** idOrSlug pro qual o fetch resolveu — chave de validade do dado. */
  key: string;
  pipeline: PipelineDetail | null;
  runs: PipelineRunSummary[];
  error: string | null;
}

export function PipelineDetailModal({ pipelineIdOrSlug, open, onClose, onChanged }: Props) {
  const [load, setLoad] = useState<LoadState | null>(null);
  // Aba selecionada é keyed pelo pipeline — trocar de pipeline volta pra Etapas.
  const [tabSel, setTabSel] = useState<{ key: string; tab: TabValue } | null>(null);
  const [runSheetId, setRunSheetId] = useState<string | null>(null);
  const [editStep, setEditStep] = useState<NormalizedStep | null>(null);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [runDemand, setRunDemand] = useState<Demand | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const flashToast = (msg: string, ms = 6000) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), ms);
  };

  useEffect(() => {
    if (!open || !pipelineIdOrSlug) return;
    let mounted = true;
    // Resolve por id OU slug; runs filtram por pipeline_id uuid (o endpoint
    // /v1/pipeline-runs não aceita slug).
    // OTIMIZAÇÃO (DEM-2045): quando o identificador já é um UUID (caso comum —
    // a listagem abre o modal com p.id), os dois fetches são INDEPENDENTES e
    // disparam em PARALELO (Promise.all) em vez de em série (await getPipeline →
    // await listPipelineRuns). Só serializa quando recebemos um slug, pois aí
    // o pipeline_id só existe após resolver o pipeline.
    // light: true — runs sem a coluna `config` (o grosso do payload); a aba Runs
    // só lê id/demand_id/status/tasks_*/started_at e a agregação só status/tasks_*,
    // nenhum consumidor de runs no modal lê `.config` (zero-regressão).
    (async () => {
      try {
        let p: PipelineDetail;
        let r: PipelineRunSummary[];
        if (UUID_RE.test(pipelineIdOrSlug)) {
          [p, r] = await Promise.all([
            getPipeline(pipelineIdOrSlug),
            listPipelineRuns({ pipeline_id: pipelineIdOrSlug, light: true }),
          ]);
        } else {
          p = await getPipeline(pipelineIdOrSlug);
          r = await listPipelineRuns({ pipeline_id: p.id, light: true });
        }
        if (mounted) setLoad({ key: pipelineIdOrSlug, pipeline: p, runs: r, error: null });
      } catch (err) {
        if (mounted) {
          setLoad({
            key: pipelineIdOrSlug,
            pipeline: null,
            runs: [],
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => { mounted = false; };
  }, [open, pipelineIdOrSlug]);

  const current = load && pipelineIdOrSlug && load.key === pipelineIdOrSlug ? load : null;
  const pipeline = current?.pipeline ?? null;
  const runs = current?.runs ?? [];
  const error = current?.error ?? null;
  const loading = open && pipelineIdOrSlug !== null && current === null;
  const tab: TabValue = tabSel && tabSel.key === pipelineIdOrSlug ? tabSel.tab : 'steps';
  const selectTab = (t: TabValue) => {
    if (pipelineIdOrSlug) setTabSel({ key: pipelineIdOrSlug, tab: t });
  };

  const reloadRuns = () => {
    const id = pipeline?.id;
    if (!id) return;
    listPipelineRuns({ pipeline_id: id, light: true })
      .then((r) => setLoad((cur) => (cur ? { ...cur, runs: r } : cur)))
      .catch(() => {});
  };

  // PIPE-02/03 — após publicar nova versão (edição de etapa ou "Nova versão"),
  // refetch do detalhe: versions[] ganha a nova entrada e latest_version_id já
  // aponta pra ela (backend move o ponteiro) → badge "ativa" troca; a anterior
  // vira "arquivada" (imutável). onChanged re-sincroniza o card da lista
  // (latest_version_label).
  const reloadDetail = () => {
    const key = pipelineIdOrSlug;
    if (!key) return;
    getPipeline(key)
      .then((p) => setLoad((cur) => (
        cur && cur.key === key ? { ...cur, pipeline: p, error: null } : cur
      )))
      .catch(() => {});
  };

  const handleVersionSaved = (message: string) => {
    flashToast(message);
    reloadDetail();
    onChanged?.();
  };

  useSupabaseRealtime(
    { table: 'pipeline_runs', filter: pipeline?.id ? `pipeline_id=eq.${pipeline.id}` : undefined },
    reloadRuns,
  );

  // Switch ativar/desativar — otimista com revert graceful (§7.6: o PATCH do
  // backend ativa pós-restart; até lá o erro é exibido e o estado volta).
  const handleToggleActive = async (next: boolean) => {
    if (!pipeline) return;
    const prev = pipeline.is_active;
    const apply = (active: boolean) => setLoad((cur) => (
      cur && cur.pipeline ? { ...cur, pipeline: { ...cur.pipeline, is_active: active } } : cur
    ));
    apply(next);
    try {
      await patchPipeline(pipeline.id, { is_active: next });
      onChanged?.();
    } catch (err) {
      apply(prev);
      flashToast(
        `Não foi possível ${next ? 'ativar' : 'desativar'} o pipeline: `
        + `${err instanceof Error ? err.message : 'erro desconhecido'}. `
        + '(PATCH /v1/pipelines/:id pode ainda não estar ativo no backend.)',
      );
    }
  };

  const activeVersion = pipeline
    ? (pipeline.versions.find((v) => v.id === pipeline.latest_version_id) ?? pipeline.versions[0])
    : undefined;
  const steps = normalizeSteps(activeVersion?.config);

  const aggregated = pipeline?.id ? aggregateRuns(runs).get(pipeline.id) : undefined;
  const totalRuns = aggregated?.total ?? runs.length;
  const completedRuns = aggregated?.completed ?? 0;
  const failedRuns = aggregated?.failed ?? 0;
  const cancelledRuns = aggregated?.cancelled ?? 0;
  const executingRuns = aggregated?.executing ?? 0;
  const pendingRuns = aggregated?.pending ?? 0;
  const blockedRuns = aggregated?.blocked ?? 0;
  const otherRuns = aggregated ? residualOthers(aggregated) + aggregated.unknown : 0;
  // PIPE-05 — % sucesso = completos/(completos+falhos); canceladas FORA.
  const pct = successPct(completedRuns, failedRuns);
  const avgDuration = avgCompletedDuration(runs);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent size="xl" className="p-0 max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header — nome + slug (copiar) + switch + meta scope/versão ativa */}
        <div className="px-6 pt-6 pb-4 border-b border-raios-border">
          <DialogHeader>
            <div className="flex items-center gap-2 flex-wrap pr-6">
              <DialogTitle className="text-xl truncate">{pipeline?.name ?? 'Pipeline'}</DialogTitle>
              {pipeline && (
                <>
                  <code className="text-xs text-raios-muted">{pipeline.slug}</code>
                  <CopyIdButton value={pipeline.slug} title="Copiar slug" />
                  <span className="inline-flex items-center gap-2 ml-2" onClick={(e) => e.stopPropagation()}>
                    <Switch
                      checked={pipeline.is_active}
                      onCheckedChange={handleToggleActive}
                      aria-label={`${pipeline.is_active ? 'Desativar' : 'Ativar'} pipeline`}
                    />
                    <span className={`text-xs font-medium ${pipeline.is_active ? 'text-raios-success-text' : 'text-raios-muted'}`}>
                      {pipeline.is_active ? 'ativado' : 'desativado'}
                    </span>
                  </span>
                </>
              )}
            </div>
            {pipeline?.description && (
              <DialogDescription className="mt-1">{pipeline.description}</DialogDescription>
            )}
          </DialogHeader>
          {pipeline && (
            <div className="flex items-center gap-6 mt-3">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-raios-muted">scope</p>
                <p className="text-sm font-mono text-raios-text">{pipeline.scope_label}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-raios-muted">versão ativa</p>
                <p className="text-sm font-mono text-raios-text">
                  {activeVersion ? `v${activeVersion.version}` : '—'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Tabs strip */}
        <div className="px-6 pt-3 border-b border-raios-border">
          <Tabs items={TABS} value={tab} onChange={(v) => selectTab(v as TabValue)} />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading && <Skeleton className="h-96 w-full" />}
          {error && (
            <div className="rounded-lg border border-raios-danger/30 bg-raios-danger/10 p-4 text-sm text-raios-danger-text">
              Erro: {error}
            </div>
          )}

          {!loading && pipeline && (
            <>
              {tab === 'steps' && (
                <div className="space-y-3">
                  <p className="text-xs text-raios-muted">
                    Etapas executadas em sequência (DAG). Clique em <strong>editar</strong> para
                    ajustar o <strong>executor</strong> (agente ou skill), o <strong>timeout</strong>{' '}
                    e o <strong>retry</strong> de cada etapa — a edição cria uma nova versão (imutável).
                  </p>
                  <Card className="p-0 overflow-hidden border border-raios-border">
                    {steps.length === 0 ? (
                      <div className="px-4 py-6 text-sm text-raios-muted italic text-center">
                        Sem etapas configuradas na versão ativa.
                      </div>
                    ) : (
                      steps.map((s) => (
                        <StepRow key={s.key} step={s} onEdit={() => setEditStep(s)} />
                      ))
                    )}
                  </Card>
                </div>
              )}

              {tab === 'versions' && (
                <div className="space-y-2">
                  {pipeline.versions.length === 0 ? (
                    <p className="text-sm text-raios-muted">Nenhuma versão publicada ainda.</p>
                  ) : (
                    pipeline.versions.map((v) => {
                      const isActive = v.id === pipeline.latest_version_id;
                      return (
                        <Card
                          key={v.id}
                          className={`p-4 border ${isActive ? 'border-raios-accent/50' : 'border-raios-border'}`}
                        >
                          <div className="flex items-start gap-4">
                            <span className="font-mono font-semibold text-raios-accent-text shrink-0">
                              v{v.version}
                            </span>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-raios-text-secondary">
                                {v.changelog ?? 'Sem changelog.'}
                              </p>
                              <p className="text-xs text-raios-muted mt-1">
                                {new Date(v.published_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                            <span className="shrink-0">
                              {v.deprecated_at
                                ? <Badge variant="danger">deprecada</Badge>
                                : isActive
                                  ? <Badge variant="success">ativa</Badge>
                                  : <Badge variant="outline">arquivada</Badge>}
                            </span>
                          </div>
                        </Card>
                      );
                    })
                  )}
                </div>
              )}

              {tab === 'runs' && (
                <div className="space-y-6">
                  {/* Demandas bloqueadas — posição canônica do wireframe (pp-run) */}
                  <BlockedDemandsSection
                    pipelineSlug={pipeline.slug}
                    onRunDemand={(d) => setRunDemand(d)}
                  />

                  <div>
                    <h4 className="text-sm font-semibold text-raios-text mb-2.5">Runs recentes</h4>
                    {runs.length === 0 ? (
                      <div className="text-center py-10">
                        <Activity className="h-8 w-8 text-raios-muted mx-auto mb-2" />
                        <p className="text-sm text-raios-muted">Nenhuma execução ainda.</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {runs.slice(0, 30).map((r) => (
                          <button
                            key={r.id}
                            type="button"
                            onClick={() => setRunSheetId(r.id)}
                            className="block w-full text-left"
                          >
                            <Card className="p-3 border border-raios-border hover:border-raios-accent/50 transition">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                  <CopyIdButton value={r.id} title="Copiar ID da run" />
                                  <code className="font-mono text-xs text-raios-muted">
                                    RUN-{r.id.slice(0, 4)}
                                  </code>
                                  <code className="font-mono text-xs text-raios-text-secondary" title={`demand_id ${r.demand_id}`}>
                                    {r.demand_id.slice(0, 8)}
                                  </code>
                                  <span className={`text-xs font-semibold ${runStatusTone(r.status)}`}>
                                    {runStatusLabel(r.status)}
                                  </span>
                                </div>
                                <div className="text-xs text-raios-muted shrink-0 font-mono">
                                  {r.started_at && new Date(r.started_at).toLocaleString('pt-BR')}
                                  {' · '}{r.tasks_done}/{r.tasks_planned} tasks
                                </div>
                              </div>
                            </Card>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {tab === 'metrics' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                    {[
                      { label: 'Total de runs', value: totalRuns, tone: 'text-raios-text' },
                      { label: 'Executando', value: executingRuns, tone: 'text-blue-600 dark:text-blue-400' },
                      { label: 'Pendentes', value: pendingRuns, tone: 'text-raios-warning-text' },
                      { label: 'Bloqueados', value: blockedRuns, tone: 'text-raios-warning-text' },
                      { label: 'Completos', value: completedRuns, tone: 'text-raios-success-text' },
                      { label: 'Falhos', value: failedRuns, tone: 'text-raios-danger-text' },
                      { label: 'Cancelados', value: cancelledRuns, tone: 'text-raios-muted' },
                    ].map((m) => (
                      <Card key={m.label} className="p-4 border border-raios-border">
                        <p className={`text-2xl font-mono ${m.tone}`}>{m.value}</p>
                        <p className="text-[10px] text-raios-muted uppercase tracking-wider mt-1">{m.label}</p>
                      </Card>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Card className="p-4 border border-raios-accent/30">
                      <p className="text-2xl font-mono text-raios-success-text">
                        {pct === null ? '—' : `${pct}%`}
                      </p>
                      <p className="text-[10px] text-raios-muted uppercase tracking-wider mt-1">Taxa de sucesso</p>
                    </Card>
                    <Card className="p-4 border border-raios-accent/30">
                      <p className="text-2xl font-mono text-raios-text">{avgDuration ?? '—'}</p>
                      <p className="text-[10px] text-raios-muted uppercase tracking-wider mt-1">Tempo médio do pipeline</p>
                    </Card>
                  </div>
                  {otherRuns > 0 && (
                    <Card className="p-3 border border-raios-warning/30 bg-raios-warning/10">
                      <p className="text-xs text-raios-warning-text">
                        <span className="font-mono font-semibold">{otherRuns}</span>{' '}
                        run{otherRuns === 1 ? '' : 's'} em status fora do enum canônico
                        (defesa anti-desync UI/backend).
                      </p>
                    </Card>
                  )}
                  <p className="text-xs text-raios-muted">
                    Taxa de sucesso = <strong>completos / (completos + falhos)</strong>
                    {pct !== null && ` = ${completedRuns} / ${completedRuns + failedRuns} ≈ ${pct}%`}.
                    Runs <strong>canceladas</strong> são terminais, mas <strong>não contam como
                    falha</strong> (cancelar ≠ falhar) — ficam fora do cálculo.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer — Fechar + Nova versão */}
        <div className="px-6 py-3 border-t border-raios-border flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button variant="default" onClick={() => setNewVersionOpen(true)} disabled={!pipeline}>
            <GitBranch className="h-4 w-4" />
            Nova versão
          </Button>
        </div>

        {/* Toast inline (erro graceful do switch etc.) */}
        {toast && (
          <div className="absolute bottom-16 right-4 z-[460] max-w-md rounded-lg border border-raios-border bg-raios-elevated px-4 py-3 shadow-raios-lg text-sm text-raios-text">
            {toast}
          </div>
        )}
      </DialogContent>

      {/* Sheet de run — resumo + timeline de etapas + "Abrir demanda" (já canônico) */}
      <PipelineRunDetailSheet
        runId={runSheetId}
        open={runSheetId !== null}
        onClose={() => setRunSheetId(null)}
      />

      {/* Modais empilhadas */}
      {pipeline && (
        <PipelineStepEditModal
          step={editStep}
          open={editStep !== null}
          onClose={() => setEditStep(null)}
          pipelineId={pipeline.id}
          activeConfig={activeVersion?.config}
          onSaved={handleVersionSaved}
        />
      )}
      {pipeline && (
        <PipelineNewVersionModal
          open={newVersionOpen}
          onClose={() => setNewVersionOpen(false)}
          pipelineSlug={pipeline.slug}
          pipelineId={pipeline.id}
          activeVersionLabel={activeVersion ? activeVersion.version : null}
          activeConfig={activeVersion?.config}
          onSaved={handleVersionSaved}
        />
      )}
      {pipeline && (
        <RunDemandModal
          open={runDemand !== null}
          onClose={() => setRunDemand(null)}
          pipelineId={pipeline.id}
          demand={runDemand}
          onStarted={(msg) => {
            flashToast(msg);
            reloadRuns();
          }}
        />
      )}
    </Dialog>
  );
}
